<script lang="ts" setup>
import { ref, watch } from 'vue';
import type { UploadCustomRequestOptions, UploadFileInfo, UploadSettledFileInfo } from 'naive-ui';
import { axiosInstance } from '@sa/axios';
import { nanoid } from '@sa/utils';
import { useBoolean } from '@sa/hooks';
import _ from 'lodash';
import { Base64 } from 'js-base64';
import { findAll as findAllAttachmentType } from '@/service/api/base/biz-attachment-type';
import { doDelete, findAll as findAllAttachment } from '@/service/api/base/attachment';
import { upload } from '@/service/request';
import { generatePresignedUrl, saveRemoteFile } from '@/service/api/base/file';
import { addFilter } from '@/utils/common';
import { getUrl } from '@/utils/attachmentUtil';
import type { Api } from '@/typings/api';

defineOptions({
  /** 上传组件 */
  name: 'BasicUpload'
});

/** 上传组件Props */
export interface BasicUploadProps {
  /** 表单对应数据表名称 */
  entityName: string;
  /** 需要显示的附件类型，如果为空，则根据entityName获取所有附件类型显示 */
  typeCodes?: string[];
  /** 关联数据对象ID,disabled=true时可以为数组，其他情况为string */
  dataId?: string | string[];
  /** 是否支持多选文件 */
  // multiple?: boolean;
  /** 是否支持拖拽上传 */
  // drag?: boolean;
  disabled?: boolean;
  /** 是否显示类型名称 */
  showTitle?: boolean;
  /** 上传到OSS存储，默认为true */
  oss?: boolean;
}
const showModal = ref(false);
const previewImageUrl = ref('');
const props = withDefaults(defineProps<BasicUploadProps>(), {
  // multiple: false,
  // drag: false,
  typeCodes: undefined,
  dataId: undefined,
  disabled: false,
  showTitle: true,
  oss: true
});

/** 获取URL */
const otherUrl = JSON.parse(import.meta.env.VITE_OTHER_SERVICE_BASE_URL);
const { bool: loading, setTrue: startLoading, setFalse: endLoading } = useBoolean(false);

/** 临时数据ID，props.dataId为空时使用 */
const tempDataId = nanoid();

const model = defineModel<string>('value');

/** EntityName对应所有附件类型 */
const attachmentTypes = ref<
  | (Api.Base.BizAttachmentType & {
      children?: UploadFileInfo[];
    })[]
  | null
>(null);
/** 附件列表 */
const attachments = ref<Api.Base.Attachment[]>([]);
/** 新上传附件列表，key为upload组件自动生成id */
const uploadAttachments = ref<Record<string, Api.Base.Attachment>>({});

watch(
  [() => props.dataId, () => props.typeCodes],
  async (val, old) => {
    let fetchAttachments = true;
    if (attachmentTypes.value === null || _.difference(val[1] ?? [], old?.[1] ?? []).length > 0) {
      startLoading();
      await getAttachmentTypes();
      await getAttachments();
      mergeAttachment();
      fetchAttachments = false;
    }
    if (val[0]) {
      if (!props.disabled && typeof props.dataId === 'string') {
        model.value = props.dataId;
      }
      if (fetchAttachments && val[0] !== old[0]) {
        startLoading();
        await getAttachments();
        mergeAttachment();
      }
    } else {
      model.value = tempDataId;
    }
    endLoading();
  },
  {
    immediate: true
  }
);

/** 获取EntityName对应所有附件类型 */
async function getAttachmentTypes() {
  const params: Api.Common.CommonSearchParams = {
    column: ['id', 'typeCode', 'typeName', 'accept', 'maxSize'],
    filter: [
      {
        column: 'genEntityMain.entityName',
        value: props.entityName,
        type: 'andEq'
      }
    ]
  };
  if (props.typeCodes && props.typeCodes.length > 0) {
    addFilter(params.filter, {
      column: 'typeCode',
      value: props.typeCodes,
      type: 'andIn'
    });
  }
  const { data } = await findAllAttachmentType(params);
  // 根据props.typeCodes排序
  if (data?.content && data.content.length > 0 && props.typeCodes && props.typeCodes.length > 0) {
    data.content = data.content.sort((a, b) => {
      const indexA = props.typeCodes?.indexOf(a.typeCode) || -1;
      const indexB = props.typeCodes?.indexOf(b.typeCode) || -1;
      return indexA - indexB; // 按照typeCodes的顺序排序
    });
  }
  attachmentTypes.value = data?.content ?? [];
}

/** 获取附件列表 */
async function getAttachments() {
  if (!props.dataId || !props.dataId.length) {
    attachments.value = [];
    return;
  }

  const params: Api.Common.CommonSearchParams = {
    column: [
      'id',
      'bizAttachmentType.id',
      'url',
      'thUrl',
      'size',
      'ext',
      'platform',
      'contentType',
      'originalFilename',
      'createTime'
    ],
    filter: [
      {
        column: 'objectId',
        value: props.dataId,
        type: typeof props.dataId === 'string' ? 'andEq' : 'andIn'
      },
      {
        column: 'bizAttachmentType.id',
        value: attachmentTypes.value?.map(item => item.id),
        type: 'andIn'
      }
    ]
  };
  const { data } = await findAllAttachment(params);
  if (props.typeCodes && props.typeCodes.length > 0) {
    attachments.value = data?.content ?? [];
  } else {
    attachments.value = data?.content ?? [];
  }
}
/** 合并附件到附件类型 */
function mergeAttachment() {
  if (attachments.value.length > 0) {
    attachmentTypes.value?.forEach(async type => {
      type.children = await Promise.all(
        attachments.value
          .filter(item => item.bizAttachmentType.id === type.id)
          .map(async item => {
            return {
              id: item.id,
              name: item.originalFilename,
              status: 'finished',
              url: await getUrl(item),
              thumbnailUrl: item.thUrl ? await getUrl(item, true) : null,
              size: item.size,
              ext: item.ext,
              type: item.contentType
            } as UploadFileInfo;
          })
      );
    });
  }
}
/**
 * 删除文件
 *
 * @param params 文件信息
 */
async function handleRemove({ file }: { file: UploadFileInfo }) {
  await doDelete(file.id);
  window.$message?.success('删除成功');
}

/**
 * 自定义上传
 *
 * @param file 文件
 */
function handleUpload({ file, data, onFinish, onError, onProgress }: UploadCustomRequestOptions) {
  if (file.file) {
    if (props.oss) {
      generatePresignedUrl({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        objectId: data.objectId,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        bizAttachmentType: data.bizAttachmentType,
        mime: file.type ?? '',
        filename: file.name
      })
        .then(async res => {
          if (res.data?.url) {
            const uploadResult = res.data;
            const response = await axiosInstance.put(uploadResult.url, file.file, {
              headers: uploadResult.headers,
              onUploadProgress: progressEvent => {
                onProgress({
                  percent:
                    (progressEvent.progress ?? progressEvent.loaded / (progressEvent.total ?? file.file!.size)) * 100
                });
              }
            });
            if (response.status === 200) {
              const fileResult = await saveRemoteFile({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                bizAttachmentType: data.bizAttachmentType,
                filename: uploadResult.filename
              });
              window.$message?.success('上传成功');
              uploadAttachments.value[file.id] = fileResult.data;
              onFinish();
            } else {
              console.warn('🚀 ~ handleUpload ~ response:', response);
              window.$message?.error('上传失败');
              onError();
            }
          }
        })
        .catch(error => {
          window.$message?.error(error.message);
          onError();
        });
    } else {
      upload('/file/upload', file.file, data, {
        onUploadProgress: progressEvent => {
          onProgress({ percent: (progressEvent.loaded / (progressEvent.total ?? file.file!.size)) * 100 });
        }
      })
        .then(res => {
          // console.log('🚀 ~ upload ~ res:', res);
          window.$message?.success('上传成功');
          uploadAttachments.value[file.id] = res.data;
          onFinish();
        })
        .catch(error => {
          console.log('🚀 ~ upload ~ error:', error);
          onError();
        });
    }
  }
}

function handlePreview(
  file: UploadSettledFileInfo,
  detail: {
    event: MouseEvent;
  }
) {
  detail.event.preventDefault();
  console.log('file', file);
  // if (file.type === 'image/jpeg') {
  //   showModal.value = true;
  //   previewImageUrl.value = file.url || '';
  // } else {
  const finalUrl = `${otherUrl.preview}/onlinePreview?url=${encodeURIComponent(Base64.encode(file.url as string))}`;
  window.open(finalUrl);
  // }
}

/**
 * 上传成功回调
 *
 * 将上传成功后的附件信息保存到附件类型列表中
 *
 * @param param0
 */
function handleFinish({ file }: { file: UploadFileInfo }) {
  if (uploadAttachments.value[file.id]) {
    const attachment = uploadAttachments.value[file.id];
    // for (const type of attachmentTypes.value) {
    //   if (type.id === attachment.bizAttachmentType.id) {
    //     for (const item of type.children!) {
    //       Object.assign(item, {
    //         id: attachment.id,
    //         name: attachment.originalFilename,
    //         status: 'finished',
    //         url: otherUrl.file + attachment.url,
    //         thumbnailUrl: otherUrl.file + attachment.thUrl,
    //         size: attachment.size,
    //         ext: attachment.ext,
    //         type: attachment.contentType
    //       });
    //     }
    //     break;
    //   }
    // }
    return {
      id: file.id,
      name: attachment.originalFilename,
      status: 'finished',
      url: otherUrl.file + attachment.url,
      thumbnailUrl: otherUrl.file + attachment.thUrl,
      size: attachment.size,
      ext: attachment.ext,
      type: attachment.contentType
    } as UploadFileInfo;
  }
  return file;
}
</script>

<template>
  <div class="flex flex-col flex-1">
    <NSpin :show="loading" size="small">
      <!-- 附件类型列表 -->
      <template v-for="type in attachmentTypes" :key="type.id">
        <NUpload
          v-if="showTitle || !disabled || (type.children && type.children.length > 0)"
          v-model:file-list="type.children"
          list-type="image"
          abstract
          :show-remove-button="!disabled"
          show-download-button
          :data="{
            objectId: model!,
            bizAttachmentType: type.id
          }"
          :accept="type.accept"
          :max="type.maxSize"
          :show-preview-button="true"
          :custom-request="handleUpload"
          @finish="handleFinish"
          @remove="handleRemove"
          @preview="handlePreview"
        >
          <NCard
            class="mb-12px"
            content-style="padding: 0"
            header-class="!p-10px fw-bold !text-14px"
            :segmented="{
              content: true
            }"
          >
            <template v-if="showTitle || !disabled" #header>
              <NSpace :justify="showTitle ? 'space-between' : 'end'" align="center">
                <span v-if="showTitle" class="fw-500 !text-14px">{{ type.typeName }}</span>
                <NUploadTrigger v-if="!disabled" #="{ handleClick }" abstract>
                  <NButton @click="handleClick">
                    <template #icon>
                      <icon-mdi-cloud-upload />
                    </template>
                    上传
                  </NButton>
                </NUploadTrigger>
              </NSpace>
            </template>
            <NUploadFileList v-if="type.children && type.children.length > 0" />
          </NCard>
        </NUpload>
      </template>
    </NSpin>
    <NModal v-model:show="showModal" preset="card" style="width: 600px">
      <img :src="previewImageUrl" style="width: 100%" />
    </NModal>
  </div>
</template>

<style scoped></style>
